import type { ReactNode } from 'react';
import { HARBOR_FACTS } from '@/lib/harbor-facts';
import devicesPng from '@/assets/devices.png';
import devicesCalendarPng from '@/assets/devices_calendar.png';
import devicesExpensesPng from '@/assets/devices_expenses.png';
import harborSsChatPng from '@/assets/harbor_ss_chat.png';
import harborSsExportsPng from '@/assets/harbor_ss_exports.png';

export type HeroCopy = {
  title: ReactNode;
  subtitle: string;
  image: string;
  imageAlt: string;
  screenshot?: boolean;
  duo?: boolean;
  priceLine?: string;
};

const PRICE_LINE = `${HARBOR_FACTS.annualPriceLabel} · ${HARBOR_FACTS.monthlyPriceLabel}`;

export const DEFAULT_HERO: HeroCopy = {
  title: (
    <>
      Co-parenting
      <br />
      made <span className='text-cream'>easy.</span>
    </>
  ),
  subtitle:
    'What the $300-a-year apps charge for, only $49.99 a year. Works solo or with both parents.',
  image: devicesPng,
  imageAlt: 'Harbor app on multiple devices',
};

export const HERO_VARIANTS: Record<string, HeroCopy> = {
  expenses: {
    title: (
      <>
        Split child
        <br />
        expenses <span className='text-cream'>easily.</span>
      </>
    ),
    subtitle:
      'Track receipts, request reimbursements, and keep a clear record of who paid what. Works solo or with both parents.',
    image: devicesExpensesPng,
    imageAlt: 'Harbor expense tracker and receipt scanner on two phones',
    duo: true,
    priceLine: PRICE_LINE,
  },
  'expenses-price': {
    title: (
      <>
        $49.99 a year.
        <br />
        Split kid <span className='text-cream'>expenses.</span>
      </>
    ),
    subtitle:
      'Track receipts, request reimbursements, and keep a clear record of who paid what. Works solo or with both parents.',
    image: devicesExpensesPng,
    imageAlt: 'Harbor expense tracker and receipt scanner on two phones',
    duo: true,
    priceLine: HARBOR_FACTS.monthlyPriceLabel,
  },
  'expenses-snap': {
    title: (
      <>
        Snap a receipt.
        <br />
        Split it <span className='text-cream'>fairly.</span>
      </>
    ),
    subtitle:
      'Harbor reads the amount, merchant, and date. Request the split and keep the record — solo or with both parents.',
    image: devicesExpensesPng,
    imageAlt: 'Harbor expense tracker and receipt scanner on two phones',
    duo: true,
    priceLine: PRICE_LINE,
  },
  'expenses-solo': {
    title: (
      <>
        Track kid expenses
        <br />
        even if they <span className='text-cream'>won&apos;t join.</span>
      </>
    ),
    subtitle:
      'Start a clear ledger on your own. Invite later — the record is already there.',
    image: devicesExpensesPng,
    imageAlt: 'Harbor expense tracker and receipt scanner on two phones',
    duo: true,
    priceLine: PRICE_LINE,
  },
  'expenses-keyword': {
    title: (
      <>
        The co-parent
        <br />
        <span className='text-cream'>expense</span> app.
      </>
    ),
    subtitle:
      'Built for splitting child costs — receipts, reimbursements, and a record both of you can see.',
    image: devicesExpensesPng,
    imageAlt: 'Harbor expense tracker and receipt scanner on two phones',
    duo: true,
    priceLine: PRICE_LINE,
  },
  calendar: {
    title: (
      <>
        Parenting time,
        <br />
        kept in <span className='text-cream'>sync.</span>
      </>
    ),
    subtitle:
      'A shared custody calendar both parents can actually follow. Works solo or together.',
    image: devicesCalendarPng,
    imageAlt: 'Harbor shared custody calendar and messaging on two phones',
    duo: true,
    priceLine: PRICE_LINE,
  },
  'calendar-weekend': {
    title: (
      <>
        Who has the kids
        <br />
        this <span className='text-cream'>weekend?</span>
      </>
    ),
    subtitle:
      'A shared custody calendar both parents can actually follow. Works solo or together.',
    image: devicesCalendarPng,
    imageAlt: 'Harbor shared custody calendar and messaging on two phones',
    duo: true,
    priceLine: PRICE_LINE,
  },
  chat: {
    title: (
      <>
        Co-parent chats,
        <br />
        kept <span className='text-cream'>calm.</span>
      </>
    ),
    subtitle:
      'Documented messaging with optional Temperature Check so heated texts do not go out as-is.',
    image: harborSsChatPng,
    imageAlt: 'Harbor co-parent messaging with Temperature Check',
    screenshot: true,
    priceLine: PRICE_LINE,
  },
  'chat-tone': {
    title: (
      <>
        Check the temperature
        <br />
        before you <span className='text-cream'>send.</span>
      </>
    ),
    subtitle:
      'Temperature Check scores a draft and offers a calmer rewrite so heated texts do not go out as-is.',
    image: harborSsChatPng,
    imageAlt: 'Harbor co-parent messaging with Temperature Check',
    screenshot: true,
    priceLine: PRICE_LINE,
  },
  coparent: {
    title: (
      <>
        Co-parenting
        <br />
        without the <span className='text-cream'>chaos.</span>
      </>
    ),
    subtitle:
      'Messages, expenses, and a custody calendar in one place — documented and exportable.',
    image: devicesPng,
    imageAlt: 'Harbor app on multiple devices',
    priceLine: PRICE_LINE,
  },
  court: {
    title: (
      <>
        Court-ready records.
        <br />
        <span className='text-cream'>$49.99</span> a year.
      </>
    ),
    subtitle:
      'Export messages, expenses, and the calendar for your attorney. If a court order names another app, use that app.',
    image: harborSsExportsPng,
    imageAlt: 'Harbor court-ready record export',
    screenshot: true,
    priceLine: HARBOR_FACTS.monthlyPriceLabel,
  },
  ofw: {
    title: (
      <>
        Court-app features.
        <br />
        Without court-app <span className='text-cream'>pricing.</span>
      </>
    ),
    subtitle:
      'Documented messages, expenses, and a calendar you can export — $49.99/year after a 7-day trial. Use the named app if a court order requires one.',
    image: harborSsExportsPng,
    imageAlt: 'Harbor court-ready record export',
    screenshot: true,
    priceLine: HARBOR_FACTS.monthlyPriceLabel,
  },
  'never-married': {
    title: (
      <>
        You don&apos;t need a divorce
        <br />
        to need a <span className='text-cream'>system.</span>
      </>
    ),
    subtitle:
      'Expenses, a calendar, and documented messages for never-married co-parents. Works solo or together.',
    image: devicesPng,
    imageAlt: 'Harbor app on multiple devices',
    priceLine: PRICE_LINE,
  },
  toxic: {
    title: (
      <>
        Document everything.
        <br />
        Argue <span className='text-cream'>less.</span>
      </>
    ),
    subtitle:
      'Timestamped messages, expenses, and a calendar you can export when things get heated.',
    image: harborSsChatPng,
    imageAlt: 'Harbor co-parent messaging with Temperature Check',
    screenshot: true,
    priceLine: PRICE_LINE,
  },
  price: {
    title: (
      <>
        $49.99 a year.
        <br />
        Co-parenting, <span className='text-cream'>documented.</span>
      </>
    ),
    subtitle:
      'Messages, expenses, and a custody calendar in one place. Works solo or with both parents.',
    image: devicesPng,
    imageAlt: 'Harbor app on multiple devices',
    priceLine: HARBOR_FACTS.monthlyPriceLabel,
  },
};

const ALIASES: Record<string, string> = {
  expense: 'expenses',
  default: 'default',
};

export function resolveHeroCopy(hero?: string): HeroCopy {
  if (!hero) return DEFAULT_HERO;
  const key = hero.trim().toLowerCase();
  const resolved = ALIASES[key] ?? key;
  if (resolved === 'default') return DEFAULT_HERO;
  return HERO_VARIANTS[resolved] ?? DEFAULT_HERO;
}
